import { orderBy, query } from 'firebase/firestore'
import {
  Batch,
  Chapter,
  DocRef,
  Page,
  PagesParent,
  getDocs,
  pageRef,
  pagesRef,
} from '../firebase'

export const sortedPagesRef = (pagesParent: DocRef<PagesParent>) =>
  query(pagesRef(pagesParent), orderBy(`order`))

/**
 * 参考書のページを目次（チャプター順 → チャプター内のページ順）の並びに整列する。
 *
 * page.order はエディタ上でチャプターごとに採番されるため、
 * 書籍全体を order だけで並べるとチャプターをまたいで混ざり、目次と一致しない。
 * （order が同値になった場合 Firestore はドキュメントIDの昇順で解決するため
 *   一見ランダムな順序になる）
 *
 * どのチャプターにも属さないページは、URLを維持したまま到達できるよう末尾へ送る。
 */
export const sortPagesAlongChapters = (pages: Page[], chapters: Chapter[]) => {
  const chapterIndexes = new Map(
    [...chapters]
      .sort((a, b) => a.order - b.order)
      .map((chapter, index) => [chapter.id, index])
  )
  // チャプターに属さないページを送る位置
  const orphanIndex = chapterIndexes.size
  const chapterIndexOf = (page: Page) =>
    (page.chapter_id !== undefined
      ? chapterIndexes.get(page.chapter_id)
      : undefined) ?? orphanIndex
  return [...pages].sort((a, b) => {
    const difference = chapterIndexOf(a) - chapterIndexOf(b)
    return difference !== 0 ? difference : a.order - b.order
  })
}

/** どのチャプターにも属さない（チャプターが削除された）ページを抽出する */
export const getPagesWithoutChapter = (pages: Page[], chapters: Chapter[]) => {
  const chapterIds = new Set(chapters.map((chapter) => chapter.id))
  return pages.filter(
    (page) => page.chapter_id === undefined || !chapterIds.has(page.chapter_id)
  )
}

export const addPages = async (
  source: DocRef<PagesParent>,
  target: DocRef<PagesParent>,
  batch: Batch
) => {
  const sourcePagesSnapshot = await getDocs(pagesRef(source))
  const sourcePages = sourcePagesSnapshot.docs.map((doc) => doc.data())
  for (const page of sourcePages) batch.create(pageRef(target, page.id), page)
  return sourcePages.length
}

export const deletePages = async (
  target: DocRef<PagesParent>,
  batch: Batch
) => {
  const targetPagesSnapshot = await getDocs(pagesRef(target))
  const targetPageRefs = targetPagesSnapshot.docs.map((doc) => doc.ref)
  for (const ref of targetPageRefs) await batch.delete(ref)
  return targetPageRefs.length
}

export const movePages = async (
  source: DocRef<PagesParent>,
  target: DocRef<PagesParent>,
  batch: Batch
) => {
  const num = await addPages(source, target, batch)
  await deletePages(source, batch)
  return num
}

export const replacePages = async (
  source: DocRef<PagesParent>,
  target: DocRef<PagesParent>,
  batch: Batch
) => {
  await deletePages(target, batch)
  return await addPages(source, target, batch)
}

export const updatePages = async (
  source: DocRef<PagesParent>,
  target: DocRef<PagesParent>,
  batch: Batch
) => {
  const [sourcePagesSnapshot, targetPagesSnapshot] = await Promise.all([
    getDocs(pagesRef(source)),
    getDocs(pagesRef(target)),
  ])
  const sourcePages = sourcePagesSnapshot.docs.map((doc) => doc.data())
  const targetDocs = targetPagesSnapshot.docs
  const targetIds = new Set(targetDocs.map((doc) => doc.id))
  const sourceIds = new Set(sourcePages.map((p) => p.id))

  // ソースに存在しないページを削除
  for (const doc of targetDocs) {
    if (!sourceIds.has(doc.id)) {
      await batch.delete(doc.ref)
    }
  }

  // ページを作成または更新
  for (const page of sourcePages) {
    const ref = pageRef(target, page.id)
    if (targetIds.has(page.id)) {
      // 既存ページは更新（comments_count は除外して既存の値を維持）
      batch.update(ref, {
        title: page.title,
        body: page.body,
        order: page.order,
        chapter_id: page.chapter_id,
        created_by_user_at: page.created_by_user_at,
        updated_by_user_at: page.updated_by_user_at,
      })
    } else {
      // 新規ページは作成（comments_count を含める）
      batch.create(ref, {
        title: page.title,
        body: page.body,
        order: page.order,
        chapter_id: page.chapter_id,
        comments_count: page.comments_count,
        created_by_user_at: page.created_by_user_at,
        updated_by_user_at: page.updated_by_user_at,
      })
    }
  }

  return sourcePages.length
}

export const moveAndReplacePages = async (
  source: DocRef<PagesParent>,
  target: DocRef<PagesParent>,
  batch: Batch
) => {
  await deletePages(target, batch)
  return await movePages(source, target, batch)
}
